<template>
  <div
    class="tw-rounded-2xl tw-bg-white dark:tw-bg-slate-800 tw-border tw-border-slate-100 dark:tw-border-slate-700 tw-shadow tw-py-3.5 tw-flex tw-flex-col tw-gap-3"
  >
    <div
      class="tw-text-sm tw-text-slate-700 dark:tw-text-slate-50 tw-font-body tw-font-medium tw-px-4"
    >
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-gap-3"
      >
        <Dropdown
          v-model="campaignType"
          :options="campaignTypeOptions"
          optionValue="value"
          optionLabel="label"
          placeholder="All Campaigns"
          class="tw-max-w-52 tw-border tw-border-transparent tw-outline-none tw-shadow dark:tw-bg-slate-800 dark:tw-border-slate-700"
          input-class="tw-p-1 tw-pl-2 tw-text-sm tw-font-medium"
        >
          <template #value="slotProps">
            <div class="tw-flex tw-gap-2 tw-items-center">
              <i class="pi pi-megaphone tw-text-sm"></i>
              <span class="tw-capitalize">
                {{ slotProps.value ? slotProps.value : slotProps.placeholder }}
              </span>
            </div>
          </template>
        </Dropdown>
        <div class="tw-flex tw-items-center tw-gap-2">
          <Button
            icon="pi pi-sync tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm"
            class="tw-h-7 tw-shadow tw-w-8 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-sm tw-font-medium tw-font-body"
            @click="refreshData()"
          />
          <download-csv :data="formattedData">
            <Button
              label="CSV"
              icon="pi pi-download tw-mr-1.5 tw-text-sm"
              class="tw-h-7 tw-shadow tw-w-[68px] tw-px-2 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm tw-font-medium tw-font-body"
            ></Button>
          </download-csv>
        </div>
      </div>
    </div>
    <div>
      <DataTable
        :value="formattedData"
        :paginator="true"
        :rows="10"
        responsiveLayout="scroll"
        :loading="props.loading"
        class="tw-bg-none tw-bg-transparent podcaster-shows-table"
        table-class="tw-bg-none tw-bg-transparent"
        :row-class="() => 'tw-bg-none tw-bg-transparent'"
      >
        <Column field="date" header="Date" />
        <Column field="campaign_type" header="Campaign Type" />
        <Column field="title" header="Title" />
        <Column field="views">
          <template #header>
            <span class="tw-inline-flex tw-items-center tw-gap-1.5">
              <span>Reach</span>
              <i
                v-tooltip.top="'Tracked by JamX widget.'"
                class="pi pi-info-circle tw-text-xs tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-slate-200 tw-cursor-help"
              ></i>
            </span>
          </template>
        </Column>
        <Column field="plays">
          <template #header>
            <span class="tw-inline-flex tw-items-center tw-gap-1.5">
              <span>Sessions</span>
              <i
                v-tooltip.top="'Tracked by JamX widget.'"
                class="pi pi-info-circle tw-text-xs tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-slate-200 tw-cursor-help"
              ></i>
            </span>
          </template>
        </Column>
        <Column field="listeners">
          <template #header>
            <span class="tw-inline-flex tw-items-center tw-gap-1.5">
              <span>Unique Listeners</span>
              <i
                v-tooltip.top="'Tracked by JamX widget.'"
                class="pi pi-info-circle tw-text-xs tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-slate-200 tw-cursor-help"
              ></i>
            </span>
          </template>
        </Column>
        <Column field="ctr">
          <template #header>
            <span class="tw-inline-flex tw-items-center tw-gap-1.5">
              <span>CTR</span>
              <i
                v-tooltip.top="'Calculated from JamX widget plays vs. views.'"
                class="pi pi-info-circle tw-text-xs tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-slate-200 tw-cursor-help"
              ></i>
            </span>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Button from "primevue/button";
import { computed, ref, defineProps, defineEmits } from "vue";
import Dropdown from "primevue/dropdown";

const props = defineProps(["loading", "data"]);

const emit = defineEmits(["refreshData"]);

const campaignTypeOptions = [
  { label: "All", value: null },
  { label: "Publisher", value: "publisher" },
  { label: "Promotion", value: "promotion" },
  { label: "Spotify", value: "spotify" },
];

const campaignType = ref(null);

const formattedData = computed(() => {
  return props.data
    .filter(
      (item) => !campaignType.value || item.campaign_type === campaignType.value
    )
    .map((item) => ({
      ...item,
      date: item.date.split("T")[0],
      views: formatNumber(item.views),
      plays: formatNumber(item.plays),
      listeners: formatNumber(item.listeners),
      ctr: formatCTR(item.ctr),
    }));
});

const formatNumber = (num) => {
  return new Intl.NumberFormat("en-US").format(num);
};

const formatCTR = (ctr) => {
  return Number(ctr).toFixed(3);
};

const refreshData = () => {
  // Implement refresh functionality here
  emit("refreshData");
};
</script>

<style>
.podcaster-shows-table .p-paginator-bottom {
  border-color: transparent !important;
}
.podcaster-shows-table .p-datatable-thead th {
  background-color: transparent !important;
}
.podcaster-shows-table .p-paginator {
  background-color: transparent !important;
  border-color: transparent !important;
}
.podcaster-shows-table td,
.podcaster-shows-table th {
  border-color: #f1f5f9 !important; /* tw-border-slate-100 */
}
.tw-dark .podcaster-shows-table td,
.tw-dark .podcaster-shows-table th {
  border-color: #334155 !important; /* dark:tw-border-slate-700 */
}
</style>
